/**
 * Wrapper hook around workflowReducer.
 *
 * Phase 3 refactor (final):
 *  - Owns: navigation (currentStep, inputMethod), lifecycle flags,
 *          parser settings, manual/input aux, selection, results UI,
 *          serverWorkflow.
 *  - Provides typed action helpers + fetchServerWorkflow().
 *
 * Sub-hooks (useParsingFlow, useAIAnalysisFlow, useUploadFlow,
 * useDomainSelection, useDataLoaders) keep their own internal state.
 *
 * Excluded (still useState in facade): `domains`, `selectedPhones`.
 */

import { useReducer, useCallback } from 'react'
import api, { type WorkflowStateResponse } from '@/services/api'
import type { InputMethod } from '../types'
import {
  workflowReducer,
  initialWorkflowState,
  type WorkflowAction,
  type WorkflowState,
  type ResultsTab,
} from './workflowReducer'

export interface UseWorkflowStateReturn {
  state: WorkflowState

  // * Lifecycle
  markRecoveryShown: () => void
  markInitialized: () => void
  resetWorkflow: () => void
  resetWorkflowKeepProject: (projectCrmId: number | null) => void

  // * Navigation
  setStep: (step: number) => void
  setInputMethod: (method: InputMethod | null) => void

  // * Manual / input aux
  setManualUrlsText: (value: string) => void
  setManualSubsource: (value: string) => void
  setProcessingManualUrls: (value: boolean) => void

  // * Parser settings
  setDownloadAll: (value: boolean) => void
  setMinClicks: (value: number) => void
  setTopQueries: (value: number) => void

  // * Selection
  setSelectedCrmId: (value: number | null) => void
  setSelectedQueries: (value: string[]) => void
  setSelectedRegionCodes: (value: string[]) => void

  // * Results UI
  setResultsTab: (value: ResultsTab) => void
  setShowAllDomains: (value: boolean) => void

  // * Server-driven recovery
  fetchServerWorkflow: (projectCrmId: number) => Promise<WorkflowStateResponse | null>

  // * Escape hatch
  dispatch: React.Dispatch<WorkflowAction>
}

export function useWorkflowState(): UseWorkflowStateReturn {
  const [state, dispatch] = useReducer(workflowReducer, initialWorkflowState)

  // * Lifecycle
  const markRecoveryShown = useCallback(() => {
    dispatch({ type: 'MARK_RECOVERY_SHOWN' })
  }, [])

  const markInitialized = useCallback(() => {
    dispatch({ type: 'INIT_DONE' })
  }, [])

  const resetWorkflow = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  const resetWorkflowKeepProject = useCallback((projectCrmId: number | null) => {
    dispatch({ type: 'RESET_KEEP_PROJECT', projectCrmId })
  }, [])

  // * Navigation
  const setStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', step })
  }, [])

  const setInputMethod = useCallback((method: InputMethod | null) => {
    dispatch({ type: 'SET_INPUT_METHOD', method })
  }, [])

  // * Manual / input aux
  const setManualUrlsText = useCallback((value: string) => {
    dispatch({ type: 'SET_MANUAL_URLS_TEXT', value })
  }, [])

  const setManualSubsource = useCallback((value: string) => {
    dispatch({ type: 'SET_MANUAL_SUBSOURCE', value })
  }, [])

  const setProcessingManualUrls = useCallback((value: boolean) => {
    dispatch({ type: 'SET_PROCESSING_MANUAL_URLS', value })
  }, [])

  // * Parser settings
  const setDownloadAll = useCallback((value: boolean) => {
    dispatch({ type: 'SET_DOWNLOAD_ALL', value })
  }, [])

  const setMinClicks = useCallback((value: number) => {
    dispatch({ type: 'SET_MIN_CLICKS', value })
  }, [])

  const setTopQueries = useCallback((value: number) => {
    dispatch({ type: 'SET_TOP_QUERIES', value })
  }, [])

  // * Selection
  const setSelectedCrmId = useCallback((value: number | null) => {
    dispatch({ type: 'SET_SELECTED_CRM_ID', value })
  }, [])

  const setSelectedQueries = useCallback((value: string[]) => {
    dispatch({ type: 'SET_SELECTED_QUERIES', value })
  }, [])

  const setSelectedRegionCodes = useCallback((value: string[]) => {
    dispatch({ type: 'SET_SELECTED_REGION_CODES', value })
  }, [])

  // * Results UI
  const setResultsTab = useCallback((value: ResultsTab) => {
    dispatch({ type: 'SET_RESULTS_TAB', value })
  }, [])

  const setShowAllDomains = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SHOW_ALL_DOMAINS', value })
  }, [])

  /**
   * Fetches consolidated workflow state from backend.
   * Returns null on error (caller falls back to localStorage / Zustand).
   */
  const fetchServerWorkflow = useCallback(
    async (projectCrmId: number): Promise<WorkflowStateResponse | null> => {
      dispatch({ type: 'RESTORE_BEGIN' })
      try {
        const data = await api.getProjectWorkflowState(projectCrmId)
        dispatch({ type: 'RESTORE_SUCCESS', payload: data })
        return data
      } catch (error) {
        // * Network or 404 — fall back to legacy recovery paths
        console.warn('[useWorkflowState] fetchServerWorkflow failed:', error)
        dispatch({ type: 'RESTORE_FAIL' })
        return null
      }
    },
    []
  )

  return {
    state,
    markRecoveryShown,
    markInitialized,
    resetWorkflow,
    resetWorkflowKeepProject,
    setStep,
    setInputMethod,
    setManualUrlsText,
    setManualSubsource,
    setProcessingManualUrls,
    setDownloadAll,
    setMinClicks,
    setTopQueries,
    setSelectedCrmId,
    setSelectedQueries,
    setSelectedRegionCodes,
    setResultsTab,
    setShowAllDomains,
    fetchServerWorkflow,
    dispatch,
  }
}
